import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '../../utilities'
import IntentsContext from '../intents/context'

const withMessaging = (WrappedComponent) => {
    function _withMessaging(props = {}) {
        const { sendMessage, welcomeMessage } = props
        const { intents } = useContext(IntentsContext)
        const introductoryMessage = welcomeMessage
        const [loading, setLoading] = useState(false)
        const [messages, updateMessages] = useState([])
        const pendingMessage = useRef()

        const appendMessage = (newMessage) => {
            updateMessages(current => [...current, newMessage])
        }

        const callback = useCallback(async () => {
            setLoading(true)
            if(pendingMessage.current) {
                return
            }
    
            try {
                pendingMessage.current = sendMessage
                const response = await pendingMessage.current(intents.welcome, {
                    messageAttributes: {
                        hideFeedback: true,
                        hideHelp: true
                    }
                })
    
                if (!response.content) throw new Error('No welcome message')
    
                appendMessage(response)
            } catch (error) {
                console.error(error)
                appendMessage({
                    type: 'response',
                    welcome: true,
                    content: introductoryMessage,
                    hideFeedback: true
                })
            } finally {
                setLoading(false)
            }
        }, [intents.welcome, introductoryMessage, sendMessage])

        useEffect(() => {
            if (messages.length === 0 && !pendingMessage.current) {
                callback()
            }
        }, [callback, messages.length])

        async function submitMessage(input = '', options = {}) {
            const { icon, type } = options
            if (!input) return
            const message = {
                type: 'input',
                content: icon ? React.createElement(FontAwesomeIcon, { icon }) : input
            }

            appendMessage(message)
            setLoading(true)

            let response = {}
            try {
                response = await sendMessage(input, {
                    messageAttributes: {
                        hideFeedback: type && type === 'feedback',
                        hideHelp: type && type === 'help'
                    }
                })
            } catch (error) {
                if (error.message) {
                    response.content = error.message
                    response.hideFeedback = true
                }
                console.error(error)
            }

            setLoading(false)

            response.content && appendMessage(response)
        }

        return <WrappedComponent
            {...props}
            loading={loading}
            setLoading={setLoading}
            messages={messages}
            handleInput={submitMessage}
        />
    }

    const componentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    //add the rest of the props

    _withMessaging.displayName = `_withMessaging(${componentName})`
    _withMessaging.propTypes = {
        welcome: PropTypes.string,
        handleInput: PropTypes.func,
        loading: PropTypes.bool,
        messages: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.oneOf(['response', 'input']),
            content: PropTypes.node,
            handleInput: PropTypes.func,
        }))
    }

    return _withMessaging
}

export default withMessaging